import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { cn } from './cn';
import { Button } from './button';
import { Checkbox, Labeled, TextInput } from './controls';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

/** Inline so this package stays free of an icon dependency. */
function CloseIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  size?: ModalSize;
  /** Rendered after the title, inside the header row (badges, status). */
  headerExtra?: ReactNode;
  /** Sticky footer row; omit for none. */
  footer?: ReactNode;
  /** Extra classes on the scrolling body. */
  bodyClassName?: string;
  /** Extra classes on the dialog box — e.g. a width the `size` presets don't cover. */
  className?: string;
  children: ReactNode;
}

/**
 * The one dialog shell. Built on the native `<dialog>` element opened with
 * `showModal()`, which gives focus trapping, Escape-to-close, an inert page
 * behind it and a stacking order that nests correctly — all for free, and all
 * things a hand-rolled `fixed inset-0` div gets wrong.
 *
 * Mount it to open, unmount it to close: `{open && <Modal …/>}`. Escape and a
 * backdrop click both call `onClose`; the owner unmounts in response.
 *
 * Put `data-autofocus` on the control that should receive focus on open;
 * otherwise the first focusable element (usually the close button) gets it.
 */
export function Modal({
  title,
  onClose,
  size = 'md',
  headerExtra,
  footer,
  bodyClassName,
  className,
  children,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // `close` fires after a native close (a second Escape in Chrome bypasses
  // `cancel`'s preventDefault) — this stops onClose running twice.
  const closed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.open) el.showModal();
    el.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => {
      if (el.open) el.close();
    };
  }, []);

  const requestClose = () => {
    if (closed.current) return;
    closed.current = true;
    onClose();
  };

  const onCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
    // Keep the element open until the owner unmounts it, so React never sees
    // a dialog that closed behind its back.
    e.preventDefault();
    requestClose();
  };

  const onBackdropClick = (e: MouseEvent<HTMLDialogElement>) => {
    // The panel fills the dialog box, so a click whose target is the dialog
    // itself can only have landed on the ::backdrop.
    if (e.target === ref.current) requestClose();
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={onCancel}
      onClose={requestClose}
      onClick={onBackdropClick}
      className={cn(
        'm-auto w-full rounded-xl bg-white p-0 text-slate-900 shadow-xl backdrop:bg-slate-900/40',
        SIZE[size],
        className,
      )}
    >
      <div className="flex max-h-[85vh] flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2 id={titleId} className="truncate font-semibold text-slate-800">
              {title}
            </h2>
            {headerExtra}
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="shrink-0 rounded p-1 hover:bg-slate-100"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className={cn('min-h-0 flex-1 overflow-auto p-5', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}

export interface ConfirmDialogProps {
  title: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  /** The exact text the user must type before the button unlocks. */
  confirmWord: string;
  /** Label above the input; defaults to `Type "<word>" to confirm`. */
  confirmPrompt?: string;
  confirmLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  /** Blocks confirmation for reasons other than the typed word (e.g. an unticked acknowledgement). */
  disabled?: boolean;
  /**
   * An explicit acknowledgement the user must tick before confirming — for
   * consequences worse than the delete itself. Rendered as a checkbox.
   */
  acknowledge?: { label: string; checked: boolean; onChange: (checked: boolean) => void };
  error?: string | null;
  /** Shown at the start of the footer while busy (progress text). */
  footerStart?: ReactNode;
  size?: ModalSize;
  /** Set when the confirm word is the entity's name and should be retyped; case-sensitive. */
  caseSensitive?: boolean;
  children?: ReactNode;
}

/**
 * The "type NAME to confirm" pattern. The gate is the typed word — the friction
 * that matters is whatever explanation the caller puts in `children` above it.
 */
export function ConfirmDialog({
  title,
  onClose,
  onConfirm,
  confirmWord,
  confirmPrompt,
  confirmLabel = 'Delete permanently',
  busyLabel = 'Deleting…',
  busy = false,
  disabled = false,
  acknowledge,
  error,
  footerStart,
  size = 'md',
  caseSensitive = true,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const confirmed = caseSensitive
    ? typed.trim() === confirmWord
    : typed.trim().toLowerCase() === confirmWord.toLowerCase();
  const canConfirm = confirmed && !busy && !disabled && (!acknowledge || acknowledge.checked);
  const formId = useId();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canConfirm) onConfirm();
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      size={size}
      footer={
        <>
          {footerStart && <span className="mr-auto text-sm text-slate-500">{footerStart}</span>}
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="danger" disabled={!canConfirm}>
            {busy ? busyLabel : confirmLabel}
          </Button>
        </>
      }
    >
      <form id={formId} className="space-y-4" onSubmit={submit}>
        {children}
        {acknowledge && (
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <Checkbox
              checked={acknowledge.checked}
              onToggle={() => acknowledge.onChange(!acknowledge.checked)}
              label={acknowledge.label}
              className="mt-0.5"
            />
            <span>{acknowledge.label}</span>
          </label>
        )}
        <Labeled label={confirmPrompt ?? `Type "${confirmWord}" to confirm`}>
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            placeholder={confirmWord}
            data-autofocus
          />
        </Labeled>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
}
